import React, { useState, useEffect, useContext, useRef } from 'react';
import * as Icons from 'react-icons/fa';
import ReactTagInput from '@pathofdev/react-tag-input';
import { API, graphqlOperation } from 'aws-amplify';
import { toast } from 'react-toastify';
import { FaCheck } from 'react-icons/fa';
import '@pathofdev/react-tag-input/build/index.css';
import Button from '../Button';
import {
  emailCaseFile,
  mergeCases,
  updateMergedCaseSources,
  removeFromMergedCase,
  setCaseReportBreakpoints,
  generateReportPdf,
  adminDownloadCase,
} from '../../generated/graphql/mutations';
import { getMergedCase, getCasesByNo } from '../../generated/graphql/queries';
import { NotificationContext } from '../../helpers/AlertContext/AlertContext';
import PickSourcesModal from '../PickSourcesModal';
import Spinner from '../Spinner';
import PopUpConfirmation from '../PopupConfirmation';
import { emailValidator } from '../../helpers/createAccount/fieldValidation';
import TextField from '../TextField';
import ReportPhotoPicker from './photoPicker';
import { ESCORT_AD } from '../../constants';

const ReportBuilder = ({ id, caseState, setCaseState }) => {
  const caseInput = useRef();
  const [, setAlert] = useContext(NotificationContext);

  const [linkedCases, setLinkedCases] = useState([]);
  const [initialLinkedCases, setInitialLinkedCases] = useState([]);

  const [initialCheckedSources, setInitialCheckedSources] = useState([]);
  const [checkedSources, setCheckedSources] = useState([]);
  const [pickSourcesReady, setPickSourcesReady] = useState(false);
  const [selectedSharedSources, setSelectedSharedSources] = useState([]);
  const [selectedBreakpoints, setSelectedBreakpoints] = useState(caseState.breakpoints || []);
  const [savingBreakpoints, setSavingBreakpoints] = useState(false);

  const [showLinkedConfirmation, setShowLinkedConfirmation] = useState(false);

  const [loading, setLoading] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const [shareReady, setShareReady] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [shareEmail, setShareEmail] = useState('');

  useEffect(() => {
    getLinkedCases();
  }, [id]);

  const getLinkedCases = async () => {
    if (caseState.mergedParentId) {
      const {
        data: { getMergedCase: res },
      } = await API.graphql(graphqlOperation(getMergedCase, { id: caseState.mergedParentId }));

      const filteredRes = res.caseNumbers.filter(item => item !== caseState.sequentialNumber);

      setInitialCheckedSources(res.sources);
      setCheckedSources(res.sources);
      setInitialLinkedCases(filteredRes);
      setLinkedCases(filteredRes);

      getSharedSources(res.caseNumbers, res.sources);
    }
  };

  const updateLinkedCases = async () => {
    setLoading(true);

    try {
      const delArr = [];

      initialLinkedCases.forEach(item => {
        if (!linkedCases.includes(item)) {
          delArr.push(item);
        }
      });

      for (const item of delArr) {
        const delParams = { input: { mergedParentId: caseState.mergedParentId, caseNumber: item } };

        await API.graphql(graphqlOperation(removeFromMergedCase, delParams));
      }

      if (linkedCases.length > 0) {
        const caseIdsToAdd = [...linkedCases].filter(x => x);

        // mergeCases
        const mergeParams = { input: { sources: [], caseIds: [...caseIdsToAdd, caseState.caseNumber] } };
        let res = await API.graphql(graphqlOperation(mergeCases, mergeParams));

        setCaseState({ ...caseState, mergedParentId: res.data.mergeCases.mergedParentId });

        res = res.data.mergeCases.sortedCaseNumbers.filter(item => item !== caseState.sequentialNumber);

        // setCaseState mergedParentId
        setLinkedCases(res);
        setInitialLinkedCases(res);
      } else {
        setLinkedCases([]);
        setInitialLinkedCases([]);
      }

      toast.success('Successfully Saved', {
        progress: false,
        className: 'bg-green-500 text-white',
        autoClose: 1500,
        closeButton: false,
        icon: () => <FaCheck size={18} className="text-white" />,
      });

      setShowLinkedConfirmation(false);
    } catch (error) {
      const errorMessage =
        'An error occurred attempting to link cases. Only Approved, Submitted, or Resubmitted cases can be linked.';

      console.error('Error linking cases: ', error);

      setAlert({
        type: 'SET_NOTIFICATION',
        payload: {
          occurs: true,
          message: errorMessage,
          textColor: 'redText',
          borderColor: 'redBorder',
        },
      });
    }
    setLoading(false);
  };

  const getSharedSources = async (currentlyLinkedCases, currentlyCheckedSources) => {
    let sourcesArr = [];
    const params = { caseNoList: JSON.stringify(currentlyLinkedCases) };

    const {
      data: { getCasesByNo: cases },
    } = await API.graphql(graphqlOperation(getCasesByNo, params));

    // make array of sources
    cases.forEach(item => {
      sourcesArr = [...sourcesArr, ...item.webSources];
    });

    const selectedSharedSourcesArr = sourcesArr.filter(x => currentlyCheckedSources.includes(x.id));

    setSelectedSharedSources(selectedSharedSourcesArr);
  };

  const onConfirm = async () => {
    setLoading(true);
    // save sources
    const params = { input: { sources: JSON.stringify(checkedSources), mergedParentId: caseState.mergedParentId } };

    await API.graphql(graphqlOperation(updateMergedCaseSources, params));

    setLoading(false);
    setPickSourcesReady(false);

    getLinkedCases();
  };

  const sharePDF = async () => {
    try {
      setSharing(true);

      const isValidEmail = emailValidator(setAlert, shareEmail);

      if (!isValidEmail) {
        return;
      }

      await API.graphql(
        graphqlOperation(emailCaseFile, {
          email: shareEmail,
          caseId: id,
        })
      );

      toast.success('Case File Shared', {
        progress: false,
        className: 'bg-green-500 text-white',
        autoClose: 1500,
        closeButton: false,
        icon: () => <Icons.FaCheck size={18} className="text-white" />,
      });

      setShareEmail('');
      setShareReady(false);
    } catch (error) {
      console.error('Error sharing file: ', error);

      setAlert({
        type: 'SET_NOTIFICATION',
        payload: {
          occurs: true,
          message: 'An error occurred attempting to share the case',
          textColor: 'redText',
          borderColor: 'redBorder',
        },
      });
    }

    setSharing(false);
  };

  const generatePDF = async () => {
    try {
      setGeneratingPDF(true);

      const {
        data: { generateReportPdf: reportKey },
      } = await API.graphql(
        graphqlOperation(generateReportPdf, {
          id,
        })
      );

      setCaseState({ ...caseState, reportKey });

      toast.success('PDF Generated', {
        progress: false,
        className: 'bg-green-500 text-white',
        autoClose: 1500,
        closeButton: false,
        icon: () => <Icons.FaCheck size={18} className="text-white" />,
      });
    } catch (error) {
      console.error('Error generating pdf: ', error);

      setAlert({
        type: 'SET_NOTIFICATION',
        payload: {
          occurs: true,
          message: 'An error occurred attempting to generate the PDF',
          textColor: 'redText',
          borderColor: 'redBorder',
        },
      });
    }

    setGeneratingPDF(false);
  };

  const downloadCase = async () => {
    try {
      setDownloading(true);

      const {
        data: {
          adminDownloadCase: { preSignedUrl },
        },
      } = await API.graphql({
        query: adminDownloadCase,
        variables: { input: { caseId: id } },
        authMode: 'AWS_IAM',
      });

      setDownloading(false);

      window.open(preSignedUrl, '_blank');
    } catch (error) {
      console.error('Error downloading case', error);

      setAlert({
        type: 'SET_NOTIFICATION',
        payload: {
          occurs: true,
          message: 'An error occurred attempting to download the case report',
          textColor: 'redText',
          borderColor: 'redBorder',
        },
      });
    }

    setDownloading(false);
  };

  const renderCaseImages = () => {
    const { reportImages } = caseState;

    let result = [];

    if (reportImages) {
      result = [...reportImages];
    }

    for (let i = 1; i <= 3; i++) {
      const matchingIdx = result.findIndex(x => x.id === `Image${i}`);

      if (matchingIdx === -1) {
        result.push({ id: `Image${i}`, s3Key: null });
      }
    }

    return result.map(x => (
      <ReportPhotoPicker caseId={id} photoId={x.id} key={x.id} s3Key={x.s3Key} setCaseState={setCaseState} />
    ));
  };

  const updateBreakpoint = (sourceId, checked) => {
    const currentBreakpoints = [...selectedBreakpoints];

    if (checked) {
      if (!currentBreakpoints.find(x => x === sourceId)) {
        currentBreakpoints.push(sourceId);
      }
    } else {
      const matchingIdx = currentBreakpoints.findIndex(x => x === sourceId);

      if (matchingIdx !== -1) {
        currentBreakpoints.splice(matchingIdx, 1);
      }
    }

    setSelectedBreakpoints(currentBreakpoints);
  };

  const renderReportBreakpoints = () => {
    const { webSources, awardedPoints } = caseState;

    const currentBreakpoints = [...selectedBreakpoints];

    let webSourceSteps = webSources.map(x => ({
      id: x.id,
      title: `${x.website} (${x.url})`,
      break: currentBreakpoints.find(y => x.id === y),
    }));

    webSourceSteps = webSourceSteps.filter(x => {
      const match = awardedPoints.find(y => x.id === y.stepId);

      if (match && match.awardType !== 'NO_POINTS') {
        return true;
      }

      return false;
    });

    let steps = [
      {
        id: ESCORT_AD,
        title: 'Escort Ad',
        break: currentBreakpoints.find(x => x === ESCORT_AD),
      },
    ];

    if (caseState.mergedParentId && selectedSharedSources && selectedSharedSources.length) {
      const mappedSources = selectedSharedSources.map(x => ({
        id: x.id,
        title: `${x.website} (${x.url})`,
        break: !!currentBreakpoints.find(y => x.id === y),
      }));

      steps = [...steps, ...mappedSources];
    } else {
      steps = [...steps, ...webSourceSteps];
    }

    return steps.map(x => (
      <div className="flex flex-row mb-2">
        <TextField
          noRounded
          noFullFieldWidth
          fieldWidth="w-5"
          fieldHeight="h-5"
          width="w-5"
          type="checkbox"
          checked={x.break}
          onChange={e => {
            updateBreakpoint(x.id, e.target.checked);
          }}
          className="mr-4 lg:mr-1"
        />
        <span className="font-bold">&nbsp; {x.title} &nbsp;</span>
      </div>
    ));
  };

  const saveBreakpoints = async () => {
    try {
      setSavingBreakpoints(true);

      const {
        data: { setCaseReportBreakpoints: res },
      } = await API.graphql(
        graphqlOperation(setCaseReportBreakpoints, {
          input: {
            id: caseState.id,
            breakpoints: selectedBreakpoints,
          },
        })
      );

      setCaseState(res);
    } catch (error) {
      console.error('Error saving breakpoints: ', error);

      setAlert({
        type: 'SET_NOTIFICATION',
        payload: {
          occurs: true,
          message: 'Error saving breakpoints',
          textColor: 'redText',
          borderColor: 'redBorder',
        },
      });
    }

    setSavingBreakpoints(false);
  };

  return (
    <>
      <div className="mb-8">
        <h2 className="text-lg font-bold text-pursuit-gray">Generate & Share Case Report</h2>
        <div className="text-sm text-pursuit-gray">
          <p>
            Share the case information by compiling it into a formal report to provide to the authorities. You may add
            up to three photos to be included in the report. You may also combine source information from any linked
            cases. You can also add page breaks between the steps and preview the PDF before finally sharing the
            document with authorities. Be sure to seperately share MFA credentials with anyone who will need access.
          </p>
        </div>
      </div>

      {pickSourcesReady ? (
        <PickSourcesModal
          linkedCases={[...linkedCases, caseState.caseNumber]}
          checkedSources={checkedSources}
          setCheckedSources={setCheckedSources}
          initialCheckedSources={initialCheckedSources}
          loading={loading}
          onConfirm={() => {
            onConfirm();
          }}
          onCancel={() => {
            setPickSourcesReady(false);
          }}
        />
      ) : null}

      <div className="py-4 mb-4 overflow-auto">
        <h2 className="mb-2 text-lg font-bold text-pursuit-gray">Linked Cases</h2>
        <div className="flex">
          <div className="flex-none">
            <ReactTagInput
              tags={linkedCases}
              onChange={newCases => {
                setLinkedCases(newCases);
              }}
              ref={caseInput}
              placeholder="____-____"
            />
          </div>
          <div className="flex items-center">
            <Button
              height="h-8"
              className="flex items-center justify-center w-12 ml-4 border-guardian-darkblue border"
              onClick={() => {
                caseInput.current.addTag(caseInput.current?.state?.input);
              }}
            >
              <Icons.FaPlus />
            </Button>
          </div>
          <div className="flex-grow" />
        </div>

        <div className="flex flex-row w-120">
          <Button
            solidBlue
            height="h-10 w-40 mt-4"
            className="flex items-center justify-center"
            onClick={() => {
              setShowLinkedConfirmation(true);
            }}
            disabled={linkedCases.length === 0 && initialLinkedCases.length === 0}
          >
            {loading ? <Spinner className="h-5" /> : 'SAVE LINKED CASES'}
          </Button>

          <Button
            linedBlue
            height="h-10 mt-4 w-56 ml-4"
            className="flex items-center justify-center"
            onClick={() => {
              setPickSourcesReady(true);
            }}
            disabled={linkedCases.length === 0}
          >
            {loading ? <Spinner className="h-5" /> : 'CHOOSE SHARED SOURCES'}
          </Button>
        </div>
      </div>

      <div className="mb-8">
        <h2 className="text-lg font-bold text-pursuit-gray">Report Images</h2>
        <p>You can zoom and shift the image, cropping to the desired size and shape.</p>
        <div className="flex flex-wrap w-full justify-center">{renderCaseImages()}</div>
      </div>

      {/* Remove manual breakpoints in favor of auto-generated ones 
      <div className="mb-8">
        <h2 className="text-lg font-bold text-pursuit-gray">Report Breakpoints</h2>

        <div className="flex flex-col mt-4">
          <p className="mb-4">
            Check which sources should include a page break immedietly <span className="font-bold">after</span> the step
            in the final report
          </p>
          <div className="mx-2 mb-4">{renderReportBreakpoints()}</div>

          <Button linedBlue onClick={saveBreakpoints} className="w-full text-base lg:w-48" loading={savingBreakpoints}>
            SAVE BREAKPOINTS
          </Button>
        </div>
      </div> */}

      {caseState.approvedAt ? (
        <div className="flex flex-row justify-end mt-12">
          <Button
            solidBlue
            onClick={generatePDF}
            className="w-full mt-4 text-base lg:mt-0 lg:w-64"
            loading={generatingPDF}
          >
            {generatingPDF ? 'GENERATING' : 'GENERATE'} PDF
          </Button>
        </div>
      ) : (
        <p className="mt-8">Case must be approved before generating the report PDF</p>
      )}

      <div className="flex flex-row justify-end mt-6">
        {caseState.reportKey ? (
          <>
            <Button
              linedBlue
              onClick={() => setShareReady(true)}
              className="w-full mr-1 text-base lg:mr-0 lg:mt-0 lg:w-64"
            >
              SHARE PDF
            </Button>

            <Button
              linedBlue
              onClick={() => downloadCase()}
              className="w-full text-base ml-1 lg:w-64 lg:ml-6"
              loading={downloading}
            >
              DOWNLOAD PDF
            </Button>
          </>
        ) : null}
      </div>

      {shareReady ? (
        <PopUpConfirmation
          title="Share Case File"
          content={
            <div className="flex-col justify-center">
              <div>Share Case File with:</div>
              <div>
                <input
                  type="text"
                  value={shareEmail}
                  onChange={event => setShareEmail(event.target.value)}
                  placeholder="Email"
                  className="w-full h-10 px-4 mt-2 mb-4 bg-gray-100 lg:mb-0 lg:mt-0 lg:mr-4 lg:w-80"
                />
              </div>
            </div>
          }
          onCancel={() => setShareReady(false)}
          onConfirm={() => sharePDF()}
          confirmText="SEND"
          confirmLoading={sharing}
          confirmLoadingText="SENDING"
          className="w-11/12 lg:w-auto"
        />
      ) : null}

      {showLinkedConfirmation ? (
        <PopUpConfirmation
          title="Link Cases"
          content={
            <div>
              Linking cases will allow you to generate a PDF report using the combined information from multiple cases.
              When generating the PDF, you may select any of the approved additional sources from the other linked cases
              to include in the final report. However, the Personal Details and Escort Ad information cannot be
              combined. The final report will only include that data from the case used to create the PDF. The
              information from the other linked cases will be available in the report appendix.
              <br />
              <br />
              Unlinking a case will make its sources no longer be included when generating the PDF. After unlinking,
              press "GENERATE PDF" to update the PDF.
              <br />
              <br />
              Are you sure you want to save these changes?
            </div>
          }
          onConfirm={() => {
            updateLinkedCases();
          }}
          onCancel={() => {
            setShowLinkedConfirmation(false);
          }}
          confirmText="CONFIRM"
          className="w-11/12 lg:w-auto"
        />
      ) : null}
    </>
  );
};

export default ReportBuilder;
